// Core data types for the Margin Monitoring System

export type MarginStatus = 'GREEN' | 'AMBER_ALERT' | 'RED_ALERT' | 'CRITICAL' | 'HOUSE_CALL' | 'Green' | 'Amber Alert' | 'Red Alert' | 'Critical' | 'House Call';
export type PositionType = 'LONG' | 'SHORT';
export type SecurityType = 'EQUITY' | 'ETF' | 'BOND' | 'OPTION' | 'MUTUAL_FUND' | 'OTHER';
export type AlertSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export interface Position {
  id: string;
  cusip: string;
  ticker: string;
  description: string;
  securityType: SecurityType;
  positionType: PositionType;
  quantity: number;
  marketPrice: number;
  marketValue: number;
  weight: number; // as percentage of portfolio
  pershingMarginRequirement: number; // as percentage
  marginRequirement: number; // absolute value
  lcAddOn: number; // as percentage
  lcRequirement: number; // absolute value
  volatility?: number;
  averageDailyVolume?: number;
}

export interface Notification {
  id: string;
  portfolioId: string;
  severity: AlertSeverity;
  category: 'CREDIT_RATING' | 'CONCENTRATION' | 'MARKET_VOLATILITY' | 'POSITION_LIMIT' | 'EXPIRATION' | 'LIQUIDITY' | 'SECTOR' | 'CURRENCY' | 'OTHER';
  title: string;
  message: string;
  timestamp: Date;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
  isRead: boolean;
}

export interface Comment {
  id: string;
  portfolioId: string;
  userId: string;
  userRole: string;
  userName: string;
  content: string;
  category: string;
  tags?: string[];
  timestamp: Date;
  parentId?: string; // for replies
  replies?: Comment[];
}

export interface Portfolio {
  id: string;
  accountId: string;
  clientName: string;
  relationshipManager: string;
  investmentConsultant?: string;
  surplusCushion: number; // as percentage
  marginStatus: MarginStatus;
  totalAUM: number;
  totalMarginRequirement: number;
  availableMargin: number;
  accountEquity: number;
  lastUpdated: Date;
  positions: Position[];
  notifications: Notification[];
  comments: Comment[];
  // Multi-custodian context
  totalClientAUM?: number; // across all custodians
  pershingPercentage?: number; // percentage of total client AUM at Pershing
  otherCustodians?: Array<{
    name: string;
    aum: number;
  }>;
}

export interface FilterOptions {
  marginStatus?: MarginStatus[];
  relationshipManager?: string[];
  surplusCushionRange?: [number, number];
  aumRange?: [number, number];
  searchTerm?: string;
}

export interface PositionFilterOptions {
  securityType?: SecurityType[];
  positionType?: PositionType[];
  weightRange?: [number, number];
  marginRequirementRange?: [number, number];
  searchTerm?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'RELATIONSHIP_MANAGER' | 'RM' | 'IC' | 'OPERATIONS' | 'SENIOR_MANAGEMENT' | 'TRADING';
  assignedAccounts?: string[]; // for RMs and ICs
}

// API Response Types (matching the backend API)
export interface ApiPortfolioSummary {
  portfolio_id_lig: string;
  client_name: string;
  status: string | null;
  surplus_cushion: number | null;
  surplus_cushion_with_cash: number | null;
  is_margin_trading: boolean | null;
  system_is_margin_trading: boolean | null;  // System-detected value (before overrides)
  total_aum: number | null;
  pershing_requirement: number | null;
  // NEW: Separate account type fields
  cash_account_equity: number | null;
  cash_and_cash_equivalent: number | null;
  margin_account_equity: number | null;
  lc_margin_requirement: number | null;
  has_margin_account: boolean | null;
  rm_name: string | null;
  last_updated: string | null;
}

export interface ApiPosition {
  cusip: string;
  ticker: string | null;
  security_name: string;
  security_type: string | null;
  account_type: string | null;
  quantity: number | null;
  market_price: number | null;
  market_value: number | null;
  weight: number | null;
  pershing_requirement: number | null;
  lc_addon: number | null;
  position_lc_margin_requirement: number | null;
  timestamp: string | null;
}

export interface ApiPortfolioDetail {
  portfolio_id_lig: string;
  client_name: string;
  status: string | null;
  surplus_cushion: number | null;
  surplus_cushion_with_cash: number | null;
  is_margin_trading: boolean | null;
  system_is_margin_trading: boolean | null;  // System-detected value (before overrides)
  total_aum: number | null;
  pershing_requirement: number | null;
  // NEW: Separate account type fields
  cash_account_equity: number | null;
  cash_and_cash_equivalent: number | null;
  margin_account_equity: number | null;
  lc_margin_requirement: number | null;
  has_margin_account: boolean | null;
  rm_name: string | null;
  rm_email: string | null;
  rm_id: string | null;
  ic_name: string | null;
  platform: string | null;
  risk_score: string | null;
  last_updated: string | null;
  positions: ApiPosition[];
}

export interface ApiPortfolioCount {
  total: number;
  by_status: Record<string, number>;
  total_with_margin_account: number;
  timestamp: string;
}

export interface MarginAlert {
  id: number;
  portfolio_id_lig: string;
  client_name: string | null;
  current_status: string;
  current_surplus_cushion: number | null;
  surplus_cushion_with_cash: number | null;
  initial_status: string | null;
  initial_surplus_cushion: number | null;
  initial_surplus_cushion_with_cash: number | null;
  surplus_cushion_change: number | null;
  surplus_cushion_with_cash_change: number | null;
  status_change_direction: string | null;
  is_margin_trading: boolean | null;
  total_aum: number | null;
  lc_margin_requirement: number | null;
  alert_created_at: string;
  last_updated_at: string;
  status_changed_at: string | null;
  rm_name: string | null;
  rm_email: string | null;
  // Additional fields from portfolio_margin_status (not stored in margin_alerts)
  margin_account_equity: number | null;
  pershing_requirement: number | null;
  // Acknowledgment fields
  is_acknowledged: boolean | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  email_sent_at: string | null;
}

export interface AlertsCountSummary {
  total: number;
  by_status: Record<string, number>;
  timestamp: string;
}

// Comment API Types
export interface ApiCommentResponse {
  comment_id: string;
  portfolio_id_lig: string;
  user_id: string;
  user_name: string;
  user_role: string;
  content: string;
  category: string;
  tags: string[] | null;
  is_reply: boolean;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiCommentWithReplies extends ApiCommentResponse {
  replies: ApiCommentResponse[];
}

export interface ApiCommentsListResponse {
  portfolio_id: string;
  comments: ApiCommentWithReplies[];
  total_count: number;
  client_name?: string;
  surplus_cushion_with_cash?: number;
  is_margin_trading?: boolean;
  total_aum?: number;
  margin_account_equity?: number;
  lc_margin_requirement?: number;
}

export interface CommentCreateRequest {
  content: string;
  category: string;
  tags?: string[];
}

export interface ReplyCreateRequest {
  content: string;
  tags?: string[];
}

export const COMMENT_CATEGORIES = [
  'Margin Alerts',
  'Client Instructions',
  'Rebalance Notes',
  'Other'
] as const;

export type CommentCategory = typeof COMMENT_CATEGORIES[number];
